import React, { FC, useEffect } from 'react';
import TermsTemplate from './termsTemplate';
import { Button, Link } from '@amzn/storm-ui';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  acceptToken,
  selectAcceptTermsStatus,
  selectTermsForTokenStatus,
} from './redux';
import styled from 'styled-components';
import { ApiResourceState } from 'src/shared/api';
import { useBundle } from '@amzn/react-arb-tools';
import { AppDispatch } from 'src/shared/state/store';
import { DynamicText } from 'src/shared/components/DynamicText';
import {
  ACCEPT_TERMS_TRANSLATION_KEY,
  TERMS_AGREEMENT_AMAZON_ADVERTISING_AGREEMENT_LINK_TEXT,
  TERMS_AGREEMENT_DSP_ADVERTISING_AGREEMENT_LINK_TEXT,
  TERMS_AGREEMENT_MESSAGE,
} from './constants';
import {
  DSP_TERMS_PAGE_URL,
  TERMS_PAGE_URL,
} from 'src/shared/features/account-registration/components/RegistrationFooter/constants';

const MaxWidthDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const ResizeableWidthDiv = styled.div`
  width: 80vw;
`;

const AgreementDiv = styled.div`
  display: flex;
  margin: auto;
  justify-content: flex-end;
`;

type AgreementButtonProps = {
  token: string;
};
const AgreementButton: FC<AgreementButtonProps> = ({ token }) => {
  const [intl, isBundleLoading] = useBundle('pages.terms');
  const getTermsStatus = useSelector(selectTermsForTokenStatus);
  const submitStatus = useSelector(selectAcceptTermsStatus);
  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    if (
      submitStatus.status === ApiResourceState.SUCCEEDED ||
      submitStatus.status === ApiResourceState.FAILED
    ) {
      // scroll to top of page to show result
      window.scrollTo(0, 0);
    }
  }, [submitStatus]);
  if (getTermsStatus.status !== ApiResourceState.SUCCEEDED || isBundleLoading) {
    return null;
  }

  const linkConfig = {
    amazonAdsTermsLink: {
      link: TERMS_PAGE_URL,
      translationKey: TERMS_AGREEMENT_AMAZON_ADVERTISING_AGREEMENT_LINK_TEXT,
    },
    amazonDspTermsLink: {
      link: DSP_TERMS_PAGE_URL,
      translationKey: TERMS_AGREEMENT_DSP_ADVERTISING_AGREEMENT_LINK_TEXT,
    },
  };
  return (
    <>
      <DynamicText
        message={intl.getMessage(TERMS_AGREEMENT_MESSAGE)}
        resolveComponent={(part) => {
          const partConfig = linkConfig[part as keyof typeof linkConfig];
          return (
            <Link href={partConfig.link}>
              {intl.getMessage(partConfig.translationKey)}
            </Link>
          );
        }}
      />
      <AgreementDiv>
        <Button
          loading={submitStatus.status === ApiResourceState.LOADING}
          disabled={submitStatus.status !== ApiResourceState.IDLE}
          onClick={() => dispatch(acceptToken(token))}
          variant="primary"
        >
          {intl.getMessage(ACCEPT_TERMS_TRANSLATION_KEY)}
        </Button>
      </AgreementDiv>
    </>
  );
};

const TermsAgreementPage: FC = () => {
  const { token } = useParams();
  if (token === undefined) {
    return null;
  }
  return (
    <MaxWidthDiv>
      <ResizeableWidthDiv>
        <TermsTemplate termsDocument="termsAgreement" fetchParams={{ token }} />
        <AgreementButton token={token} />
      </ResizeableWidthDiv>
    </MaxWidthDiv>
  );
};

export default TermsAgreementPage;
